import { Component, OnInit } from "@angular/core";
import { FormGroup, UntypedFormBuilder, Validators } from "@angular/forms";
import { BnUsuarioService } from "@bndes/bndes-ux4/bn-usuario";
import { Router } from "@angular/router";
import { BnLoadingService, MessageChannelService, messageLevel} from "@bndes/bndes-ux4/bndes-angular-utils";
import { BnAlertsService } from "@bndes/bndes-ux4/bn-alerts";
import {
  Arquivo,
  LabelTipoConteudo,
  TipoConteudo,
} from "src/app/model/arquivo.model";
import { leilaoLoteExemplo } from "src/app/model/leilao.model";
import { FileUtilsService } from "src/app/shared/file-utils.service";
import { OcorrenciaValidacaoVO } from "src/app/shared/ocorrencia-validacao.vo";
import { ProtocoloArquivoService } from "src/app/shared/services/protocolo-arquivo.service";
import { LeilaoService } from "../leilao.service";
import { RemoverAlertasService } from "src/app/shared/services/remover-alertas.service";
import Utils from "src/app/shared/fgi-utils";

@Component({
  selector: "app-leilao-lote",
  templateUrl: "./leilao-lote.component.html",
  styleUrls: ["./leilao-lote.component.scss"],
  standalone: false
})
export class LeilaoLoteComponent implements OnInit {
  private cnpjUsuario: string;

  arquivo: Arquivo;
  labelTipoConteudo: string;
  filecontentTemplate = "";
  loteLeilaoForm: FormGroup;
  ocorrencias: OcorrenciaValidacaoVO[] = [];
  enviado = false;
  leiloes = [];

  constructor(
    private router: Router,
    private service: LeilaoService,
    private fileUtils: FileUtilsService,
    private bnUsuarioService: BnUsuarioService,
    private formBuilder: UntypedFormBuilder,
    private bnAlertsService: BnAlertsService,
    private loadingService: BnLoadingService,
    private messageService: MessageChannelService,
    private protocoloService: ProtocoloArquivoService,
    private removerAlertasService: RemoverAlertasService
  ) {}

  ngOnInit(): void {
    this.createReactiveForm();

    this.bnUsuarioService.usuario$.subscribe((usuario) => {
      this.cnpjUsuario = usuario.cnpj;
    });
  }

  createReactiveForm() {
    this.loteLeilaoForm = this.formBuilder.group({
      arquivo: ["", Validators.required],
      opcaoEnvio: ["", Validators.required]
    });
  }

  voltar() {
    this.router.navigate(["/leilao/pesquisa"]);
  }

  baixarExemploLeiloes() {
    let conteudoArquivoExemplo: string = leilaoLoteExemplo;

    this.fileUtils.exportarArquivo(
      "exemplo-leiloes-operacoes.json",
      this.trocaAgenteLogado(conteudoArquivoExemplo),
      FileUtilsService.APPLICATION_JSON
    );
  }

  private trocaAgenteLogado(exemploStr): string {
    const exemplo = JSON.parse(exemploStr);
    exemplo.cnpjAgenteFinanceiro = this.cnpjUsuario;
    return JSON.stringify(exemplo, null, 2);
  }

  onSubmit() {
    if (!this.arquivo.value) {
      this.bnAlertsService.alertaErro("Erro", "O arquivo não possui conteúdo.");
      return;
    }

    this.loteLeilaoForm.disable();
    this.enviado = true;
    this.enviarLeiloes();
  }

  async enviarLeiloes() {
    this.loadingService.bloqueiaTela();

    this.leiloes = [];
    this.ocorrencias = [];

    const msgService = this.messageService;
    const loteLeiloes = JSON.parse(this.arquivo.value);

    let totalItens = 0;

    // preenche a data de envio

    if (this.arquivo.tipoConteudo === TipoConteudo.LEILAO) {
      this.leiloes = loteLeiloes.lotes;
      totalItens = this.leiloes.length;
    }

    const opcaoEnvioStr = this.loteLeilaoForm.get("opcaoEnvio").value;
    const apenasValidar = opcaoEnvioStr === "validar";

    const exibirMensagemSucesso = (res) => {
      this.loadingService.liberaTela();
      const mensagemSucesso = `${res.mensagem} Id de protocolo: ${res.idProtocolo}`;
      msgService.addMessage({
        title: "Sucesso",
        type: "CRUD_OPERATION",
        level: messageLevel.SUCCESS,
        message: mensagemSucesso,
      });
      this.router.navigate(["/leilao/pesquisa"]);
      this.limpar();
    };

    const exibirMensagemErro = (err) => {
      this.loadingService.liberaTela();
      this.ocorrencias = this.service.ocorrencias;
      this.limpar();
    };

    this.protocoloService
      .enviarArquivo(this.arquivo, this.arquivo.tipoConteudo, apenasValidar)
      .subscribe(exibirMensagemSucesso, exibirMensagemErro);
  }

  limpar() {
    this.arquivo = null;

    this.loteLeilaoForm.reset();
    this.loteLeilaoForm.markAsPristine();
    this.loteLeilaoForm.enable();
    this.loteLeilaoForm.updateValueAndValidity();

    this.enviado = false;
  }

  onFileChange(event) {
    this.leiloes = [];
    this.ocorrencias = [];
    this.removerAlertasService.removerTodos();

    this.loteLeilaoForm.markAsDirty();
    const reader = new FileReader();

    if (event.target.files && event.target.files.length) {
      const file = event.target.files[0];

      if (!this.suportaTamArquivo(file.size)) {
        console.log(`Arquivo muito grande ${file.size} bytes`);
        // A mensagem abaixo não aparece se for chamada diretamente aqui
        // Acredito (jmaur) que é por esta em tratamento de evento
        // Colocá-la em um processamento assíncrono (como o setTimeout()) resolve a questão
        setTimeout(() => {
          this.limpar();
          this.bnAlertsService.alertaErro(
            "Erro",
            `O arquivo ${file.name} é muito grande. Divida em lotes menores e envie um arquivo por vez.`
          );
        }, 0);
        return;
      }

      reader.readAsText(file, "UTF-8");
      reader.onload = () => {
        let conteudoArquivo: string = "";
        if (typeof reader.result === "string") {
          conteudoArquivo = reader.result;
        } else {
          conteudoArquivo = this.fileUtils.lerConteudo(reader);
        }

        // evitar erros com estas aspas estranhas
        conteudoArquivo = conteudoArquivo.replace("“", '"').replace("”", '"');

        const lote = this.validarArquivo(file.name, file.type, conteudoArquivo);

        this.arquivo = new Arquivo();
        this.arquivo.filename = file.name;
        this.arquivo.filetype = file.type;
        this.arquivo.value = conteudoArquivo;
        this.arquivo.tipoConteudo = lote.tipoConteudo;
        this.labelTipoConteudo = LabelTipoConteudo.get(
          this.arquivo.tipoConteudo
        );

        this.loteLeilaoForm.updateValueAndValidity();
      };
    }
  }

  private suportaTamArquivo(fileSizeBytes: number): boolean {
    // tenta descobrir o quanto temos de memória
    let memSizeBytes = undefined;
    let perf: any = window.performance;
    if (perf.memory) {
      memSizeBytes = perf.memory.jsHeapSizeLimit;
    }

    if (memSizeBytes) {
      const limiteBytes = Math.round(memSizeBytes / 30); // Número mágico, apurado com algumas medições
      console.log(
        `limiteArquivo - estimado pela memória livre. fileSizeBytes: ${fileSizeBytes} limiteBytes: ${limiteBytes}`
      );
      return fileSizeBytes < limiteBytes;
    } else {
      const limiteBytes = 64 * 1024 * 1024; // 64 MB deve caber em uma máquina com 8GB
      console.log(
        `limiteArquivo - valor fixo. fileSizeBytes: ${fileSizeBytes} limiteBytes: ${limiteBytes}`
      );
      return fileSizeBytes < limiteBytes;
    }
  }

  private validarArquivo(nomeArquivo, tipoArquivo, conteudoArquivo): Lote {
    if (tipoArquivo !== "application/json") {
      this.bnAlertsService.alertaErro(
        "Erro",
        `O sistema suporta apenas arquivos JSON - '${nomeArquivo}' --> '${tipoArquivo}'.`
      );
      this.limpar();
      return null;
    }

    if (conteudoArquivo === null || conteudoArquivo === "") {
      this.bnAlertsService.alertaErro("Erro", "O arquivo está vazio.");
      this.limpar();
      return null;
    }

    if (!Utils.isValidJson(conteudoArquivo)) {
      this.bnAlertsService.alertaErro(
        "Erro",
        "Não foi possível validar o arquivo JSON. " +
          "Verifique se há erros de sintaxe no arquivo, como vírgulas mal colocadas ou chaves não fechadas. " +
          "Uma sugestão é utilizar um dos vários sites de validação JSON disponíveis na web."
      );
      this.limpar();
      return null;
    }

    const conteudo = JSON.parse(conteudoArquivo);

    const lote = FabricaLote.criar(conteudo);

    if (lote === null) {
      this.bnAlertsService.alertaErro(
        "Erro",
        "O arquivo não contém novos leiloes."
      );
      this.limpar();
      return null;
    }

    const erro = lote.validar();
    if (erro !== null) {
      this.bnAlertsService.alertaErro("Erro", erro);
      this.limpar();
      return null;
    }

    return lote;
  }

}

abstract class Lote {
  conteudo: any;

  constructor(public tipoConteudo: TipoConteudo, conteudo: any) {
    this.conteudo = conteudo;
  }

  abstract validar();
}

class LoteLeiloes extends Lote {
  constructor(conteudo: any) {
    super(TipoConteudo.LEILAO, conteudo);
  }

  validar() {
    const leiloesSemId = this.conteudo.lotes.filter(
      (ope) => ope.valorArrematadoLote == null
    );
    if (leiloesSemId.length > 0) {
      return "Existe leilao sem o atributo 'valorArrematadoLote' no arquivo.";
    }

    return null;
  }
}

class FabricaLote {
  static criar(conteudo: any): Lote {
    if (conteudo.lotes) {
      return new LoteLeiloes(conteudo);
    } else {
      return null;
    }
  }
}
